import { normalizeDirection } from './format';
import type { CallDirection } from './types';

/** Who spoke a transcript line: our side, or the person we were talking to. */
export type TranscriptSide = 'agent' | 'customer';

/**
 * Which side of the conversation a Telnyx transcription track belongs to.
 *
 * The track is relative to the LEG being transcribed, not to the conversation,
 * and the calling service transcribes a different leg in each direction:
 *
 *  - Inbound: the caller's leg. `inbound` is audio arriving from the customer,
 *    `outbound` is what we send them. Track and speaker line up.
 *  - Outbound: the leg the browser dialled — OUR leg (the browser dials Telnyx
 *    directly, so the leg that reaches `telnyxOutgoingWebhookController` and
 *    gets `transcription_start` is the agent's own). Audio arriving on it is
 *    the AGENT, and what Telnyx sends to it is the CUSTOMER. Mirror image.
 *
 * Reading the track the same way in both directions put every outbound call's
 * words under the wrong speaker — the customer's lines attributed to the team,
 * the team's to the customer.
 *
 * Returns null for a line with no usable track, which the caller shows as
 * neither side rather than guessing.
 */
export function transcriptSideForTrack(
  track: string | null | undefined,
  direction: CallDirection | string | null | undefined,
): TranscriptSide | null {
  const normalized = track?.trim().toLowerCase();
  if (normalized !== 'inbound' && normalized !== 'outbound') return null;

  const fromUs = normalized === 'outbound';
  return normalizeDirection(direction) === 'outbound'
    ? fromUs
      ? 'customer'
      : 'agent'
    : fromUs
      ? 'agent'
      : 'customer';
}
